import React, { useState, useRef, useEffect, useCallback } from "react";
import { SectionTab } from "./propertyConfig/SectionTab";
import { usePropertyStore } from "@/store/usePropertyStore";
import type { PropertyDefinition } from "@/types/property";

interface SectionPropertiesConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * SectionPropertiesConfigDialog Component
 *
 * Dialog for configuring beam section properties
 * Features:
 * - Section property configuration (Material, Section Type, Designation)
 * - Draggable header
 * - Apply, Apply & Exit, Close actions
 * - Follows Theme Format README patterns
 */
export const SectionPropertiesConfigDialog: React.FC<
  SectionPropertiesConfigDialogProps
> = ({ isOpen, onClose }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);

  // Property state
  const [propertyData, setPropertyData] = useState<Partial<PropertyDefinition>>(
    {
      P_ID: "",
      beamIds: [],
      material: "None",
    }
  );

  const {
    addProperty,
    updateProperty,
    getProperty,
    applyPropertyToBeams,
    editingPropertyId,
    setEditingPropertyId,
  } = usePropertyStore();
  const properties = usePropertyStore((state) => state.properties);

  // Generate next available P_ID
  const generateNextPID = useCallback(() => {
    const existingPIDs = properties
      .map((p) => p.P_ID)
      .filter((pid) => /^\d+$/.test(pid)) // Only numeric PIDs
      .map((pid) => parseInt(pid, 10));

    const maxPID = existingPIDs.length > 0 ? Math.max(...existingPIDs) : 0;
    return (maxPID + 1).toString();
  }, [properties]);

  // Load property if editing
  useEffect(() => {
    if (editingPropertyId) {
      // EDIT MODE: Load existing property
      const property = getProperty(editingPropertyId);
      if (property) {
        console.log("📝 Edit Mode - Loading property:", property);
        setPropertyData({
          ...property,
          // Ensure beamIds is always an array
          beamIds: property.beamIds || [],
        });
      }
    } else {
      // CREATION MODE: Reset for new property with auto-generated P_ID
      const newPID = generateNextPID();
      console.log("✨ Creation Mode - Auto-generated P_ID:", newPID);
      setPropertyData({
        P_ID: newPID,
        beamIds: [],
        material: "None",
      });
    }
  }, [editingPropertyId, getProperty, generateNextPID]);

  // Center dialog on open
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const dialogWidth = 576; // w-[576px]
      const dialogHeight = dialogRef.current.offsetHeight || 600;
      setPosition({
        x: Math.max(0, (window.innerWidth - dialogWidth) / 2),
        y: Math.max(0, (window.innerHeight - dialogHeight) / 2),
      });
    }
  }, [isOpen]);

  // Dragging handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".close-button")) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dialogHeight = dialogRef.current?.offsetHeight || 600;
      const newX = Math.max(
        0,
        Math.min(e.clientX - dragStart.x, window.innerWidth - 576)
      );
      const newY = Math.max(
        0,
        Math.min(e.clientY - dragStart.y, window.innerHeight - dialogHeight)
      );
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const handlePropertyUpdate = useCallback(
    (updates: Partial<PropertyDefinition>) => {
      setPropertyData((prev) => ({ ...prev, ...updates }));
      setErrorMessage(null);
    },
    []
  );

  const handleApply = (): boolean => {
    if (!propertyData.material || propertyData.material === "None") {
      setErrorMessage("Material is required");
      return false;
    }

    if (!propertyData.sectionType) {
      setErrorMessage("Section Type is required");
      return false;
    }

    if (!propertyData.sectionProperties?.designation) {
      setErrorMessage("Section Designation is required");
      return false;
    }

    if (!propertyData.P_ID || propertyData.P_ID.trim() === "") {
      setErrorMessage("Property ID is required");
      return false;
    }

    setErrorMessage(null);

    if (editingPropertyId) updateProperty(editingPropertyId, propertyData);
    else addProperty(propertyData as PropertyDefinition);

    if (propertyData.beamIds?.length) {
      applyPropertyToBeams(propertyData.P_ID!, propertyData.beamIds);
    }

    return true;
  };

  const handleCreateAndExit = () => {
    const ok = handleApply();
    if (ok) handleClose();
  };

  const handleClose = useCallback(() => {
    setEditingPropertyId(null);
    onClose();
  }, [setEditingPropertyId, onClose]);

  // Keyboard handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="fixed bg-white rounded-lg shadow-xl w-[576px] z-50"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        {/* Draggable Header */}
        <div
          className="bg-blue-600 text-white px-4 py-3 rounded-t-lg cursor-grab active:cursor-grabbing select-none flex justify-between items-center"
          onMouseDown={handleMouseDown}
        >
          <h2 className="text-xl font-semibold">
            {editingPropertyId
              ? "Edit Section Properties"
              : "New Section Properties"}
          </h2>
          <button
            className="close-button text-white hover:text-gray-200 p-1 rounded transition-colors"
            onClick={handleClose}
            type="button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Section Content (No Tabs) */}
        <div className="overflow-y-auto max-h-[70vh]">
          <div className="p-6">
            <SectionTab
              property={propertyData}
              onUpdate={handlePropertyUpdate}
              isEditing={!!editingPropertyId}
            />
          </div>

          {/* Error Message Block */}
          {errorMessage && (
            <div className="px-6 -mt-2 mb-2">
              <p className="text-sm text-red-600 font-medium">{errorMessage}</p>
            </div>
          )}

          {/* Footer Actions */}
          <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
            {editingPropertyId ? (
              <>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!!errorMessage}
                  className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors 
    ${errorMessage ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  Update
                </button>

                <button
                  type="button"
                  onClick={handleCreateAndExit}
                  disabled={!!errorMessage}
                  className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors 
    ${errorMessage ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  Update & Exit
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleCreateAndExit}
                disabled={!!errorMessage}
                className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors 
    ${errorMessage ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
              >
                Create & Exit
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
